/**
 * Zweck: Beschreibt die TypeScript-Bausteine für den Frontend-Bereich frontend/src/app/modules/shop/components/view-result-table/shop.view-result-table.compontent.ts.
 */
import {ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {View} from '../../../../models/view';
import {Artikel} from '../../../../models/artikel';
import {ArtikelWithAktionen} from '../../../../models/artikel-with-aktionen';
import {AppSettingsService} from '../../../../services/app.app_settings.service';
import {AppViewService} from '../../../../services/app.view.service';
import {
  SharedViewFieldSelectorComponent
} from '../../../shared/components/view-field-selector/shared.view-field-selector.component';
import {AppMobileService} from '../../../../services/app.mobile.service';
import {ShopViewInterface} from '../../interfaces/shop.view-interface';
import {ArtikelAktion} from '../../../../models/artikel-aktion';
import {AppConfigService} from '../../../../services/app.config.service';

/**
 * Zweck: Beschreibt class ShopViewResultTableCompontent und ihren Daten- oder Funktionsvertrag.
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    // tslint:disable-next-line:component-selector
    selector: 'shop-view-result-table',
    templateUrl: 'shop.view-result-table.compontent.html',
    styleUrls: ['shop.view-result-table.compontent.scss'],
    standalone: false
})
// tslint:disable-next-line:component-class-suffix
export class ShopViewResultTableCompontent implements OnInit, OnDestroy {
  @Input() viewcomp: ShopViewInterface;
  @Input() view: View;
  @Input() entries: BehaviorSubject<Artikel[]>;
  @Input() visible_artikel_aktionen: BehaviorSubject<ArtikelWithAktionen>;
  @Input() visible_artikel_einkaufsstatistik: BehaviorSubject<any>;
  @Input() infoText: any;
  @Input() showView = false;
  @Input() fieldsSaveable = true;
  active_fields: string[];
  column_order = SharedViewFieldSelectorComponent.fields_order_table;

  private view_fields = new BehaviorSubject<string[]>([]);
  view_fields$ = this.view_fields.asObservable();

  has_bez_field = new BehaviorSubject<boolean>(true);

  /** Aktionsfelder werden beim Popup-Modus durch das Symbol am Artikelnamen ersetzt. */
  readonly popupHiddenFields = ['aktionen_button', 'aktionen_first_stage',
    'aktionen_second_stage', 'aktionen_third_stage'];
  protected preservedActionFields: string[] = [];

  /**
   * Liefert die für den Feldwähler ausgeblendeten Felder.
   * @return Felder der Ansicht und bei Bedarf die bisherigen Aktionsspalten.
   */
  getHiddenFields(): string[] {
    const hidden = this.view.settings.hidden_fields || [];
    return this.cfg.domainconfig?.shop_aktionen_popup ? hidden.concat(this.popupHiddenFields) : hidden;
  }

  /**
   * Bewahrt die vorherige Aktionsspalten-Auswahl beim Speichern im Popup-Modus.
   * @return Benutzerfelder einschließlich der vorübergehend ausgeblendeten Aktionsfelder.
   */
  protected getFieldsToSave(): string[] {
    return this.cfg.domainconfig?.shop_aktionen_popup
      ? [...this.active_fields, ...this.preservedActionFields.filter(field => !this.active_fields.includes(field))]
      : this.active_fields;
  }

/**
 * Beschreibt Methode constructor und ihren Rückgabevertrag.
 */
  constructor(public vsvc: AppViewService, public sett: AppSettingsService, public msvc: AppMobileService,
              public cfg: AppConfigService) {
  }


/**
 * Beschreibt Methode showEinkaufsStatistikWeb und ihren Rückgabevertrag.
 */
  showEinkaufsStatistikWeb(artikel: Artikel) {
    const statRegistry = this.visible_artikel_einkaufsstatistik.value;
    statRegistry[artikel.Artikelstamm + artikel.ArtikelNummer] = artikel.EinkaufsStatistikWeb;
    this.visible_artikel_einkaufsstatistik.next(statRegistry);
  }


/**
 * Beschreibt Methode hideEinkaufsStatistikWeb und ihren Rückgabevertrag.
 */
  hideEinkaufsStatistikWeb(artikel: Artikel) {
    const statRegistry = this.visible_artikel_einkaufsstatistik.value;
    delete statRegistry[artikel.Artikelstamm + artikel.ArtikelNummer];
    this.visible_artikel_einkaufsstatistik.next(statRegistry);
  }

/**
 * Beschreibt Methode showAktion und ihren Rückgabevertrag.
 */
  showAktion(artikel: Artikel, aktionen: ArtikelAktion[]) {
    const a = new ArtikelWithAktionen();
    a.artikel = artikel;
    a.aktionen = aktionen;
    const aktionenRegistry: any = this.visible_artikel_aktionen.value;
    aktionenRegistry[artikel.Artikelstamm + artikel.ArtikelNummer] = a;
    this.visible_artikel_aktionen.next(aktionenRegistry);
  }

/**
 * Beschreibt Methode hideAktionen und ihren Rückgabevertrag.
 */
  hideAktionen(artikel: Artikel) {
    const aktionenRegistry: any = this.visible_artikel_aktionen.value;
    delete aktionenRegistry[artikel.Artikelstamm + artikel.ArtikelNummer];
    this.visible_artikel_aktionen.next(aktionenRegistry);
  }

/**
 * Beschreibt Methode loadFields und ihren Rückgabevertrag.
 */
  loadFields() {
    const ucfg = this.vsvc.cfg_gui_table_columns.value;

    let tmp_fields = [];

    if (ucfg && ucfg[this.view.knoten_id] && typeof ucfg[this.view.knoten_id] !== 'undefined') {
      tmp_fields = ucfg[this.view.knoten_id];

    } else {
      tmp_fields = this.view.settings.table_default_fields;
      if (!tmp_fields || tmp_fields.length == 0) {
        tmp_fields = [
          'aktionen_button',
          'logo1',
          'bezeichnung',
          'minablauf',
          'darreichungsform',
          'hersteller',
          'pzn',
          'einkaufspreis',
          'verkaufspreis',
          'rezeptzeichen',
          'istarzneimittel',
          'status'
        ];
      }
    }

    this.preservedActionFields = tmp_fields.filter(field => this.popupHiddenFields.includes(field));
    const active_fields = [];
    this.column_order.forEach(c => {
      if (c == 'bezeichnung') {
        if (this.showView) {
          active_fields.push('view');
        }
      }
      if (tmp_fields.includes(c)) {
        active_fields.push(c);
      }

    });
    this.active_fields = active_fields;


    this.updateView(false);
  }

/**
 * Beschreibt Methode saveFields und ihren Rückgabevertrag.
 */
  saveFields() {
    if (this.fieldsSaveable) {
      const tmp = this.vsvc.cfg_gui_box_columns.value;
      tmp[this.view.knoten_id] = this.getFieldsToSave();
      this.sett.setUserSettingValue('GUI_ShopViewTableColumns', tmp).subscribe();
    }
  }

/**
 * Beschreibt Methode trackByArtNr und ihren Rückgabevertrag.
 */
  trackByArtNr(a, b: Artikel) {
    return b.ArtikelNummer;
  }

/**
 * Beschreibt Methode trackByValue und ihren Rückgabevertrag.
 */
  trackByValue(a, b) {
    return b;
  }

/**
 * Beschreibt Methode ngOnDestroy und ihren Rückgabevertrag.
 */
  ngOnDestroy(): void {
  }

/**
 * Beschreibt Methode ngOnInit und ihren Rückgabevertrag.
 */
  ngOnInit(): void {
    this.loadFields();
  }

/**
 * Beschreibt Methode hideField und ihren Rückgabevertrag.
 */
  hideField(fname) {

  }

/**
 * Beschreibt Methode updateView und ihren Rückgabevertrag.
 */
  updateView(save = true) {
    if (save) {
      this.saveFields();
    }

    this.has_bez_field.next(this.active_fields.includes('bezeichnung'));
    const fields = this.cfg.domainconfig?.shop_aktionen_popup
      ? this.active_fields.filter(field => !this.popupHiddenFields.includes(field))
      : this.active_fields;
    this.view_fields.next(fields);
  }
}
